import {
	getDealQuery,
	getDealUsers,
	getUserDealList,
} from '@dealstack/frontend/shared/dataExchange/services/project.service.ts';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { generatePath, useNavigate } from 'react-router-dom';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import { useAppSelector } from '@dealstack/frontend/app/hooks/redux.ts';
import {
	createActiveProgramme,
	createMIPDraft,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import { SummaryDealMip } from '@dealstack/shared/lib/generated';
import type { QuickCreateProgrammeValues } from '@dealstack/frontend/app/pages/Project/DealDashboard/CreateProgrammeDrawer/CreateProgrammeDrawer.types.ts';
import { createPersonLegal } from '@dealstack/frontend/app/services/Person.service';
import {
	getCapDetailsForDashboard,
	getCapTransactionsValidatationDetails,
} from '@dealstack/frontend/app/services/CapTableData.service.ts';
import {
	DEAL_ROUTES,
	MIP_ROUTES,
	PEOPLE_ROUTES,
	ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { useDealUserPermissions } from '@dealstack/frontend/app/pages/Project/DealDashboard/useDealUserPermissions.ts';

export const useDealController = () => {
	const { dealId } = useRequiredParams();
	const userState = useAppSelector((state) => state.user);
	const navigate = useNavigate();
	const [quickCreateOpen, setQuickCreateOpen] = useState(false);

	const { hasPermission: hasDealViewerPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: dealId,
		permissionRole: PermissionRole.VIEWER,
	});

	const dealRequest = useQuery({
		queryKey: [formatUnicorn(API_PATHS.DEAL_DEALID_GET, { dealId })],
		queryFn: ({ signal }) =>
			getDealQuery(formatUnicorn(API_PATHS.DEAL_DEALID_GET, { dealId }), signal),
		enabled: false,
		placeholderData: keepPreviousData,
	});
	const { refetchUser, isRefreshingPermissions } = useDealUserPermissions(
		dealId,
		hasDealViewerPermission
	);

	const getDealListRequest = useQuery({
		queryKey: [API_PATHS.DEAL_GET, userState.data],
		queryFn: ({ signal }) => getUserDealList(signal),
		enabled: !!userState.data?.id,
	});
	const getDealTeamRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.DEAL_DEALID_USERS_GET, {
				dealId,
			}),
		],
		queryFn: () =>
			getDealUsers(
				formatUnicorn(API_PATHS.DEAL_DEALID_USERS_GET, {
					dealId,
				})
			),
		enabled: false,
	});
	const createNewMIPDraftRequest = useMutation({
		mutationFn: (variables: Parameters<typeof createMIPDraft>[0]) => createMIPDraft(variables),
	});

	const capDetailsRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.CAP_DEAL_DEALID_CAP_DETAILS_GET, {
				dealId,
			}),
		],
		queryFn: () =>
			getCapDetailsForDashboard(
				formatUnicorn(API_PATHS.CAP_DEAL_DEALID_CAP_DETAILS_GET, {
					dealId,
				})
			),
		enabled: hasDealViewerPermission,
	});

	const capValidationDetails = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.CAP_DEAL_DEALID_CAP_VALIDATION_DETAILS_GET, {
				dealId,
			}),
		],
		queryFn: () =>
			getCapTransactionsValidatationDetails(
				formatUnicorn(API_PATHS.CAP_DEAL_DEALID_CAP_VALIDATION_DETAILS_GET, {
					dealId,
				})
			),
		enabled: hasDealViewerPermission,
	});

	useEffect(() => {
		if (dealId) {
			void dealRequest.refetch();
			void getDealTeamRequest.refetch();
		}
	}, [dealId]);

	const createNewMip = () => {
		if (!dealId) {
			return;
		}
		createNewMIPDraftRequest.mutate(
			{
				dealId,
			},
			{
				onSuccess: async (response) => {
					await refetchUser();
					const newMIPId = response?.payload?.id;
					const path = generatePath(
						`${ROUTES.DEAL}${DEAL_ROUTES.MIP}${MIP_ROUTES.CREATE}`,
						{
							dealId,
							mipId: newMIPId ?? '',
						}
					);
					navigate(path);
				},
			}
		);
	};

	/**
	 * The drawer route: a finished programme rather than a wizard draft. Defaults are applied
	 * without asking — the organisation's when it has them, the built-in seed otherwise — and a
	 * failure to apply them never loses the programme, which already exists by then.
	 */
	const quickCreateMip = useMutation({
		mutationFn: async (values: QuickCreateProgrammeValues) => {
			const entityId =
				values.isNewEntity && values.entityLabel
					? (
							await createPersonLegal({
								dealId,
								createArgs: { name: values.entityLabel },
							})
						).id
					: values.entityId ?? undefined;

			const created = await createActiveProgramme({
				programmeName: values.programmeName,
				entityId,
				currency: values.currency,
			});

			return created.payload?.id;
		},
		onSuccess: async (mipId) => {
			setQuickCreateOpen(false);
			await refetchUser();
			navigate(
				generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.MIP}${MIP_ROUTES.MANAGE}`, {
					dealId,
					mipId: mipId ?? '',
				})
			);
		},
	});

	const onIPAccess = (ip: SummaryDealMip) => {
		const link = generatePath(
			`${ROUTES.DEAL}${DEAL_ROUTES.MIP}${ip.state === 'DRAFT' ? MIP_ROUTES.CREATE : MIP_ROUTES.MANAGE}`,
			{
				dealId,
				mipId: ip.id ?? '',
			}
		);
		navigate(link);
	};

	const navigateToEntityWithInvalidTransactions = () => {
		const path = generatePath(
			`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.TRANSACTIONS}`,
			{
				dealId: dealId ?? null,
				personId: capValidationDetails.data?.firstEntityWithInvalidTransactionsId ?? null,
			}
		);
		navigate(path);
	};

	return {
		deal: dealRequest.data,
		refetchDealData: dealRequest.refetch,
		team: getDealTeamRequest?.data || [],
		getDealTeamRequest,
		dealId,
		createNewMip,
		quickCreateOpen,
		openQuickCreate: () => setQuickCreateOpen(true),
		closeQuickCreate: () => setQuickCreateOpen(false),
		onQuickCreate: quickCreateMip.mutate,
		isQuickCreating: quickCreateMip.isPending,
		onIPAccess,
		isRefreshingPermissions,
		deals: getDealListRequest.data?.payload,
		dealsRefetch: getDealListRequest.refetch,
		userState,
		createMipLoading: createNewMIPDraftRequest.isPending,
		capDetailsIsLoading: capDetailsRequest.isLoading,
		refreshCapDetails: capDetailsRequest.refetch,
		isAllTransactionValidInDeal: capValidationDetails.data?.isAllTransactionValidInDeal,
		firstEntityWithInvalidTransactionsId:
			capValidationDetails.data?.firstEntityWithInvalidTransactionsId,
		navigateToEntityWithInvalidTransactions,
		refetchCapValidationDetails: capValidationDetails.refetch,
	};
};
